import React, { useState } from 'react';
import styled from 'styled-components';
import { getColorScheme } from '../theme/colorSchemes';
import Rating from '../rating';
import ReviewsPanel from './ReviewsPanel';
import { FaMapMarkerAlt, FaClock, FaGlobe, FaEnvelope, FaPhone } from 'react-icons/fa';
import {
  ExpertStripContainer, ExpertCard, ExpertImg, ExpertName as StripName,
  ExpertTitle as StripTitle, ExpertRatingRow,
  QuickInfo, InfoItem, InfoIcon, InfoLabel, InfoValue, InfoText,
  OpenBadge, ClosedBadge,
  ProgressSection, RingWrap, RingText, RingCurrent, RingDivider, RingTotal,
  ProgressInfo, ProgressPhase, PhaseBadge, ProgressHint, SegBar, Seg,
} from '../ChatStyles';

const JOURNEY_STEPS = [
  { id: 'welcome', label: 'Welcome' },
  { id: 'initial', label: 'Language' },
  { id: 'gathering', label: 'Info' },
  { id: 'documents', label: 'Docs' },
  { id: 'analysis', label: 'Analysis' },
  { id: 'connect', label: 'Advisor' },
];

const ScheduleContainer = styled.div`
  max-height: ${p => p.$expanded ? '300px' : '0'};
  overflow: hidden; transition: max-height 0.3s ease;
  padding: ${p => p.$expanded ? '6px 12px' : '0 12px'};
`;

const ScheduleRow = styled.div`
  display: flex; justify-content: space-between; padding: 4px 0;
  font-size: 11px; color: ${p => p.$isToday ? '#0f172a' : '#64748b'};
  font-weight: ${p => p.$isToday ? 600 : 400};
`;

const AboutBox = styled.div`
  padding: 8px 12px; font-size: 12px; color: #64748b; line-height: 1.5;
`;

const ExpandBtn = styled.button`
  background: none; border: none; padding: 0; cursor: pointer;
  font-size: 11px; font-weight: 600; font-family: inherit;
  color: ${p => getColorScheme(p.$scheme).primary};
  &:hover { text-decoration: underline; }
`;

const ExpertSidebar = ({ scheme, expertData, conversationStep, questionnaireProgress }) => {
  const [hoursExpanded, setHoursExpanded] = useState(false);
  const [aboutExpanded, setAboutExpanded] = useState(false);
  const [reviewsOpen, setReviewsOpen] = useState(false);
  const colors = getColorScheme(scheme);

  const currentStepIndex = JOURNEY_STEPS.findIndex(s => s.id === conversationStep);
  const qCurrent = questionnaireProgress?.current || currentStepIndex + 1;
  const qTotal = questionnaireProgress?.total || 25;
  const pct = Math.min(Math.round((qCurrent / qTotal) * 100), 100);
  const circumference = 2 * Math.PI * 26;
  const dashOffset = circumference - (circumference * pct / 100);

  const phaseName = JOURNEY_STEPS[currentStepIndex]?.label || 'Getting Started';

  return (
    <ExpertStripContainer $scheme={scheme}>
      <ExpertCard>
        <ExpertImg src={expertData.imgUrl} alt={expertData.expertName} />
        <StripName>{expertData.expertName}</StripName>
        <StripTitle>{expertData.expertTilte}</StripTitle>
        <ExpertRatingRow>
          <Rating
            rating={expertData.stars}
            reviews={expertData.reviewAmount}
            onClick={expertData.reviewAmount > 0 ? () => setReviewsOpen(true) : undefined}
          />
        </ExpertRatingRow>
      </ExpertCard>

      {reviewsOpen && (
        <ReviewsPanel
          expertId={expertData.expertId}
          expertName={expertData.expertName}
          scheme={scheme}
          onClose={() => setReviewsOpen(false)}
        />
      )}

      <QuickInfo>
        {/* Unguarded, a missing address rendered an empty row whose click opened
            a Google Maps search for "undefined". */}
        {expertData.expertInfo.location?.trim() && (
          <InfoItem $scheme={scheme} onClick={() => {
            const addr = encodeURIComponent(expertData.expertInfo.location);
            window.open(`https://www.google.com/maps/search/?api=1&query=${addr}`, '_blank');
          }} style={{ cursor: 'pointer' }}>
            <InfoIcon $scheme={scheme}><FaMapMarkerAlt size={14} /></InfoIcon>
            <InfoText>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>{expertData.expertInfo.location}</InfoValue>
            </InfoText>
          </InfoItem>
        )}

        <InfoItem $scheme={scheme} onClick={() => setHoursExpanded(!hoursExpanded)} style={{ cursor: 'pointer' }}>
          <InfoIcon $scheme={scheme}><FaClock size={14} /></InfoIcon>
          <InfoText>
            <InfoLabel>Hours</InfoLabel>
            <InfoValue style={{ flexWrap: 'nowrap', whiteSpace: 'nowrap' }}>
              {expertData.workingHours?.isOpen
                ? <OpenBadge>Open</OpenBadge>
                : <ClosedBadge>Closed</ClosedBadge>}
              {' · '}
              {expertData.workingHours?.currentStatus?.replace(/^(Open|Closed) · /, '') || (expertData.expertInfo.hourClose ? `Closes ${expertData.expertInfo.hourClose}` : '')}
            </InfoValue>
          </InfoText>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{
            transform: hoursExpanded ? 'rotate(180deg)' : '', transition: 'transform 0.2s', color: '#94a3b8'
          }}>
            <path d="M2.5 4.5L6 8L9.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </InfoItem>

        <ScheduleContainer $expanded={hoursExpanded}>
          {expertData.workingHours?.schedule?.map((item, i) => (
            <ScheduleRow key={i} $isToday={item.isToday}>
              <span>{item.day}</span>
              <span style={{ color: item.hours === 'Closed' ? '#ef4444' : undefined }}>{item.hours}</span>
            </ScheduleRow>
          ))}
        </ScheduleContainer>

        {/* Only render a row when there is something in it. An adviser who has not
            supplied a phone number was otherwise given a labelled empty box, which
            reads as a broken widget rather than as "no phone on file". */}
        {expertData.expertInfo.site?.trim() && (
          <InfoItem $scheme={scheme}>
            <InfoIcon $scheme={scheme}><FaGlobe size={14} /></InfoIcon>
            <InfoText><InfoLabel>Website</InfoLabel><InfoValue>{expertData.expertInfo.site}</InfoValue></InfoText>
          </InfoItem>
        )}

        {expertData.expertInfo.email?.trim() && (
          <InfoItem $scheme={scheme}>
            <InfoIcon $scheme={scheme}><FaEnvelope size={14} /></InfoIcon>
            <InfoText><InfoLabel>Email</InfoLabel><InfoValue>{expertData.expertInfo.email}</InfoValue></InfoText>
          </InfoItem>
        )}

        {expertData.expertInfo.phone?.trim() && (
          <InfoItem $scheme={scheme}>
            <InfoIcon $scheme={scheme}><FaPhone size={14} /></InfoIcon>
            <InfoText><InfoLabel>Phone</InfoLabel><InfoValue>{expertData.expertInfo.phone}</InfoValue></InfoText>
          </InfoItem>
        )}

        {expertData.expertInfo.about && (
          <AboutBox>
            {aboutExpanded ? expertData.expertInfo.about : expertData.expertInfo.about?.slice(0, 100)}
            {expertData.expertInfo.about?.length > 100 && (
              <>
                {!aboutExpanded && '... '}
                <ExpandBtn $scheme={scheme} onClick={() => setAboutExpanded(!aboutExpanded)}>
                  {aboutExpanded ? 'Show less' : 'Read more'}
                </ExpandBtn>
              </>
            )}
          </AboutBox>
        )}
      </QuickInfo>

      <ProgressSection $scheme={scheme}>
        <RingWrap>
          <svg viewBox="0 0 58 58" style={{ width: '100%', height: '100%', transform: 'rotate(-90deg)' }}>
            <circle cx="29" cy="29" r="26" fill="none" stroke="#edf0f4" strokeWidth="5" />
            <circle cx="29" cy="29" r="26" fill="none" stroke={colors.primary} strokeWidth="5"
              strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
              style={{ transition: 'stroke-dashoffset 0.8s cubic-bezier(.4,0,.2,1)' }} />
          </svg>
          <RingText>
            <RingCurrent>{qCurrent}</RingCurrent>
            <RingDivider />
            <RingTotal>{qTotal}</RingTotal>
          </RingText>
        </RingWrap>
        <ProgressInfo>
          <ProgressPhase>
            {phaseName}
            <PhaseBadge $scheme={scheme}>Active</PhaseBadge>
          </ProgressPhase>
          <ProgressHint>
            {questionnaireProgress
              ? `Question ${qCurrent} of ~${qTotal}`
              : 'Answer questions to complete your profile'}
          </ProgressHint>
          <SegBar>
            {JOURNEY_STEPS.map((step, i) => (
              <Seg key={step.id} $scheme={scheme}
                $done={i < currentStepIndex}
                $active={i === currentStepIndex} />
            ))}
          </SegBar>
        </ProgressInfo>
      </ProgressSection>
    </ExpertStripContainer>
  );
};

export default ExpertSidebar;
